/**
 * PauseCountdownCard — renders in Settings when a subscriber has an active
 * billing pause. Shows "auto-resumes in N days" with a soft progress bar so
 * the pause never feels forgotten. Silent when there is no active pause.
 *
 * Reads `subscription_paused_until` (+ optional `subscription_paused_at`)
 * off /api/entitlements. The pause endpoint clamps at 60 days server-side.
 */
import React, { useEffect, useState } from "react";
import { fetchEntitlements } from "@/lib/api";
import { Pause, CalendarClock } from "lucide-react";
import { Link } from "react-router-dom";

function daysBetween(fromIso, toIso) {
  const a = new Date(fromIso).getTime();
  const b = new Date(toIso).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.max(0, Math.ceil((b - a) / (24 * 60 * 60 * 1000)));
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function PauseCountdownCard() {
  const [ent, setEnt] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchEntitlements()
      .then((data) => {
        if (!cancelled) {
          setEnt(data);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loaded || !ent?.subscription_paused_until) return null;

  const nowIso = new Date().toISOString();
  const daysLeft = daysBetween(nowIso, ent.subscription_paused_until);
  if (daysLeft === null) return null;

  const pausedAt = ent.subscription_paused_at || nowIso;
  const totalDays = daysBetween(pausedAt, ent.subscription_paused_until) || 60;
  const elapsed = Math.max(0, totalDays - daysLeft);
  const pct = Math.min(100, Math.round((elapsed / totalDays) * 100));
  const resumeStr = formatDate(ent.subscription_paused_until);

  const headline =
    daysLeft === 0
      ? "Auto-resuming today"
      : daysLeft === 1
      ? "Auto-resumes tomorrow"
      : `Auto-resumes in ${daysLeft} days`;

  return (
    <section
      className="border border-terracotta/60 bg-terracotta/5 p-6 sm:p-8 mb-10"
      data-testid="pause-countdown-card"
    >
      <div className="flex items-start gap-4 mb-5">
        <div className="h-10 w-10 border border-terracotta text-terracotta flex items-center justify-center flex-shrink-0">
          <Pause className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-2">
            Subscription paused
          </p>
          <h3
            className="font-serif text-2xl text-bone-100 mb-1 leading-tight"
            data-testid="pause-countdown-headline"
          >
            {headline}
          </h3>
          <p className="font-sans text-sm text-bone-400 flex items-center gap-2">
            <CalendarClock className="h-3.5 w-3.5 text-bone-500" aria-hidden="true" />
            <span data-testid="pause-countdown-resume-date">
              Next charge on {resumeStr}
            </span>
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div
        className="h-1.5 w-full bg-obsidian-900 border border-obsidian-600"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={totalDays}
        aria-valuenow={elapsed}
        aria-label={`Pause progress: ${elapsed} of ${totalDays} days elapsed`}
      >
        <div
          className="h-full bg-terracotta transition-[width] duration-500"
          style={{ width: `${pct}%` }}
          data-testid="pause-countdown-progress"
        />
      </div>
      <div className="flex items-center justify-between mt-2 font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-500">
        <span data-testid="pause-countdown-elapsed">
          {elapsed} of {totalDays} days
        </span>
        <Link
          to="/pricing"
          data-testid="pause-countdown-cancel-link"
          className="hover:text-terracotta transition-colors"
        >
          Cancel instead →
        </Link>
      </div>

      <p className="font-serif italic text-sm text-bone-400 leading-relaxed mt-6">
        Your entries stay right where they are. Nothing to do — Umbra will
        quietly resume on the date above unless you cancel first.
      </p>
    </section>
  );
}
